import { useRef, useState } from 'react';
import {
    Badge,
    BlockStack,
    Box,
    Button,
    Card,
    InlineStack,
    Typography,
} from '@pv-frontend/ui-components-web';
import { grantTermLabel, type ComplimentaryStanding, type GrantActionView } from './complimentary-standing';
import { formatDate } from './format-date';
import { ProGrantModal, reasonProblem, type GrantTermValue } from './pro-grant-modal';

/**
 * A grant or revoke commit: resolves `null` once the server has confirmed it, or readable copy for why
 * it was refused or could not be confirmed. The screen wires these to the `grantPro` / `revokePro`
 * mutations.
 */
export type GrantProAction = (term: GrantTermValue, reason: string) => Promise<string | null>;
export type RevokeProAction = (reason: string) => Promise<string | null>;

export interface ComplimentaryProPanelProps {
    vaultName: string;
    vaultIdentifier: string;
    standing: ComplimentaryStanding;
    onGrantPro?: GrantProAction;
    onRevokePro?: RevokeProAction;
}

/** The smallest window — the default a hurried operator is least surprised by. */
const DEFAULT_TERM: GrantTermValue = 'ONE_MONTH';

const confirmed = async () => null;

/** What the standing says about the current plan, for the confirm step. */
function currentPlanDetail(standing: ComplimentaryStanding): string | null {
    if (standing.kind === 'complimentary') {
        return standing.until ? `until ${formatDate(standing.until)}` : 'indefinite';
    }
    if (standing.kind === 'grant-ended') {
        return standing.ended === 'revoked' ? 'complimentary Pro revoked' : 'complimentary Pro ended';
    }
    return null;
}

/** One recorded operator write: who, when, which window, and why. Operator-only — never customer-facing. */
function GrantActionFacts({ label, action }: { label: string; action: GrantActionView }) {
    const when = formatDate(action.occurredUtc);
    return (
        <BlockStack gap="100">
            <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                {label}
            </Typography>
            <Typography variant="body-md">
                {action.term
                    ? `${grantTermLabel(action.term)} · by ${action.operator} on ${when}`
                    : `By ${action.operator} on ${when}`}
            </Typography>
            <Typography variant="body-sm" tone="text-secondary">
                {`Reason: ${action.reason}`}
            </Typography>
        </BlockStack>
    );
}

function Headline({ standing }: { standing: ComplimentaryStanding }) {
    switch (standing.kind) {
        case 'complimentary':
            return (
                <Typography variant="body-md">
                    {standing.until
                        ? `Complimentary Pro until ${formatDate(standing.until)}.`
                        : 'Complimentary Pro, indefinite — runs until an operator revokes it.'}
                </Typography>
            );
        case 'grant-ended':
            return (
                <Typography variant="body-md">
                    {standing.ended === 'revoked'
                        ? 'Complimentary Pro was revoked and has ended — this vault is on Free.'
                        : 'Complimentary Pro has ended — this vault is on Free.'}
                </Typography>
            );
        default:
            return standing.lastGrant ? null : (
                <Typography variant="body-md" tone="text-secondary">
                    This vault has never been comped.
                </Typography>
            );
    }
}

/**
 * The Complimentary Pro section of vault detail (pv-frontend#607): the grant facts the backend publishes
 * — comped or not, until when, by whom and why — and the operator's docked Grant / Revoke actions, each
 * behind a confirm step. Owns the confirm step's transient state; the commit itself is the screen's.
 */
export function ComplimentaryProPanel({
    vaultName,
    vaultIdentifier,
    standing,
    onGrantPro = confirmed,
    onRevokePro = confirmed,
}: ComplimentaryProPanelProps) {
    const [mode, setMode] = useState<'grant' | 'revoke' | null>(null);
    const [term, setTerm] = useState<GrantTermValue>(DEFAULT_TERM);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // A latch, not just state: two presses can land before a re-render disables the button, and a second
    // grant on a live comp extends it again.
    const inFlight = useRef(false);

    const open = (next: 'grant' | 'revoke') => {
        setMode(next);
        setTerm(DEFAULT_TERM);
        setReason('');
        setError(null);
    };

    const close = () => {
        if (!submitting) setMode(null);
    };

    const confirm = async () => {
        // The button is disabled for both, but a commit must never go out on a blank reason or twice.
        if (mode === null || inFlight.current || reasonProblem(reason) !== null) return;
        inFlight.current = true;
        setSubmitting(true);
        setError(null);
        const trimmed = reason.trim();
        const outcome = mode === 'grant' ? await onGrantPro(term, trimmed) : await onRevokePro(trimmed);
        inFlight.current = false;
        setSubmitting(false);
        if (outcome === null) setMode(null);
        else setError(outcome);
    };

    const { lastGrant, lastRevoke } = standing;
    const hasActions = standing.canGrant || standing.canRevoke;

    return (
        <BlockStack gap="300">
            <Typography variant="heading-sm" as="h2">
                Complimentary Pro
            </Typography>
            <Card padding="0" testID="complimentary-pro">
                <Box padding="400">
                    <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center" wrap>
                            {standing.kind === 'complimentary' ? (
                                <Badge tone="success">Complimentary</Badge>
                            ) : null}
                            <Headline standing={standing} />
                        </InlineStack>
                        {lastGrant ? (
                            <GrantActionFacts
                                label={standing.kind === 'complimentary' ? 'Granted' : 'Last grant'}
                                action={lastGrant}
                            />
                        ) : null}
                        {lastRevoke ? (
                            <GrantActionFacts
                                label={
                                    standing.revokeSupersededByGrant
                                        ? 'Earlier revoke (before the grant above)'
                                        : 'Revoked'
                                }
                                action={lastRevoke}
                            />
                        ) : null}
                    </BlockStack>
                </Box>
                <Box
                    paddingInline="400"
                    paddingBlock="300"
                    borderColor="border-card"
                    borderBlockStartWidth="0165"
                >
                    {hasActions ? (
                        <InlineStack align="flex-end" gap="200" wrap>
                            {standing.canRevoke ? (
                                <Button variant="critical" size="slim" onPress={() => open('revoke')}>
                                    Revoke
                                </Button>
                            ) : null}
                            {standing.canGrant ? (
                                <Button variant="primary" size="slim" onPress={() => open('grant')}>
                                    {standing.grantExtends ? 'Extend grant' : 'Grant Pro'}
                                </Button>
                            ) : null}
                        </InlineStack>
                    ) : (
                        <Typography variant="body-sm" tone="text-secondary">
                            {standing.grantUnavailableReason ??
                                'Complimentary Pro is not available for this vault.'}
                        </Typography>
                    )}
                </Box>
            </Card>

            <ProGrantModal
                open={mode !== null}
                mode={mode ?? 'grant'}
                onClose={close}
                vaultName={vaultName}
                vaultIdentifier={vaultIdentifier}
                currentPlan={standing.planLabel}
                currentPlanDetail={currentPlanDetail(standing)}
                grantExtends={standing.grantExtends}
                term={term}
                onTermChange={setTerm}
                reason={reason}
                onReasonChange={setReason}
                onConfirm={confirm}
                submitting={submitting}
                error={error}
            />
        </BlockStack>
    );
}
